import { SetStateAction, useCallback, useMemo } from "react";
import { LightningBoltIcon, XIcon } from "@heroicons/react/outline";

import classnames from "classnames";
import { components, OptionProps } from "react-select";
import { AdvancedSelectField } from "components";
import { ISelectOption } from "types";
import { Step } from "react-joyride";
import { twMerge } from "tailwind-merge";

export interface State {
  run: boolean;
  stepIndex: number;
  steps: Step[];
}

function Option(props: OptionProps<any>) {
  return (
    <div
      className={classnames("mt-1", {
        "selected-option-gradient": props.isSelected,
        "hovered-selected-option-gradient px-6": !props.isSelected,
      })}
    >
      <components.Option {...props}>
        <label className="line-clamp-1 cursor-pointer py-3.5 text-sm">
          {props.label}
        </label>
      </components.Option>
    </div>
  );
}

export function SkillsSelectField({
  currentSelectedSkills,
  skillOptions,
  onSkillsChange,
  setSkillSearchParam,
  className,
  mainClassName,
  badgeIcon,
  badgeLabel,
}: {
  currentSelectedSkills: ISelectOption[];
  skillOptions: ISelectOption[] | undefined;
  onSkillsChange: (value: any) => void;
  setSkillSearchParam: (value: SetStateAction<string>) => void;
  className?: string;
  mainClassName?: string;
  badgeIcon?: JSX.Element;
  badgeLabel?: string;
}) {
  const placeholder = "Search skills";
  const selectedSkills = useMemo(() => currentSelectedSkills || [], [currentSelectedSkills]);

  const getSkillKey = useCallback((value: ISelectOption["value"]) => {
    if (Array.isArray(value)) {
      return value.join("|");
    }
    return value != null ? String(value) : "";
  }, []);

  const handleRemoveSkill = useCallback(
    (skillToRemove: ISelectOption) => {
      const keyToRemove = getSkillKey(skillToRemove.value);
      const remaining = selectedSkills.filter(
        (item) => getSkillKey(item.value) !== keyToRemove
      );
      onSkillsChange(remaining);
    },
    [getSkillKey, onSkillsChange, selectedSkills]
  );

  const selectStyles = useMemo(
    () => ({
      indicatorsContainer: (provided: any) => ({
        ...provided,
        display: "flex",
        alignItems: "center",
        paddingRight: "0.25rem",
      }),
      indicatorSeparator: () => ({ display: "none" }),
      option: () => ({}),
      control: (provided: any) => ({
        ...provided,
        borderRadius: 12,
        border: "1px solid transparent",
        boxShadow: "none",
        minHeight: "44px",
        paddingLeft: "0.25rem",
      }),
      valueContainer: (provided: any) => ({
        ...provided,
        paddingLeft: "0.5rem",
        paddingRight: "0.5rem",
      }),
      multiValue: () => ({ display: "none" }),
      container: (provided: any) => ({
        ...provided,
        width: "100%",
      }),
      menu: (provided: any) => ({
        ...provided,
        zIndex: 30,
      }),
      menuList: (provided: any) => ({
        ...provided,
        paddingTop: 0,
        paddingBottom: 0,
      }),
      placeholder: (provided: any) => ({
        ...provided,
        color: "#94a3b8",
        fontWeight: 500,
      }),
    }),
    []
  );

  return (
    <div
      className={classnames(
        "w-full rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-[0_20px_45px_-30px_rgba(15,23,42,0.3)] transition focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-200",
        mainClassName
      )}
    >
      <div className="flex flex-col gap-2">
        {(badgeIcon || badgeLabel) && (
          <span className="inline-flex items-center gap-2 rounded-full bg-slate-100/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.32em] text-slate-600">
            {badgeIcon ?? <LightningBoltIcon className="h-3.5 w-3.5 text-slate-500" />}
            <span>{badgeLabel || "Skills"}</span>
          </span>
        )}
        <p className="text-sm text-slate-600">
          Pick skills to narrow down the results.
        </p>
      </div>
      <div className="mt-3">
        <AdvancedSelectField
          isMulti
          placeholder={placeholder}
          className={twMerge(classnames("w-full !pb-0", className))}
          classNames={{
            inputContainer: "mt-0",
          }}
          closeMenuOnSelect
          options={skillOptions}
          value={selectedSkills}
          onInputChange={(value) => {
            setSkillSearchParam(value);
          }}
          onChange={onSkillsChange}
          styles={selectStyles}
          components={{
            Option,
            DropdownIndicator: () => (
              <LightningBoltIcon className="mr-2 h-4 w-4 text-slate-400" />
            ),
            ClearIndicator: (props) => (
              <XIcon
                className="h-4 w-4 cursor-pointer text-slate-400 transition hover:text-slate-600"
                aria-hidden="true"
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  props.clearValue();
                }}
              />
            ),
          }}
        />
      </div>
      {selectedSkills.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2.5">
          {selectedSkills.map((skill) => (
            <button
              key={getSkillKey(skill.value)}
              type="button"
              className="group inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-white"
              onClick={() => handleRemoveSkill(skill)}
            >
              <LightningBoltIcon className="h-4 w-4 text-slate-400 group-hover:text-slate-600" />
              <span>{skill.label}</span>
              <span className="rounded-full bg-slate-200/80 p-0.5 text-slate-500 transition group-hover:bg-slate-300 group-hover:text-slate-700">
                <XIcon className="h-3.5 w-3.5" />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
